<script lang="ts">
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { KeyRound, Lock, History, Trash2 } from 'lucide-svelte';
  import { adminUsersApi, adminVerticalsApi, adminLoginsApi } from '$lib/api/admin';
  import { getErrorMessage } from '$lib/api/client';
  import SearchBar from '$lib/components/ui/SearchBar.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import UserActivityModal from './UserActivityModal.svelte';
  import { initials, avatarColor } from '$lib/utils/display';
  import { clamp, parsePercentToBps } from '$lib/utils/money';
  import type { AdminUser } from '$lib/types';

  const queryClient = useQueryClient();

  let search = $state('');
  let showForm = $state(false);
  let editingId = $state<number | null>(null);
  let formError = $state('');
  let tempPassword = $state('');

  let resetPasswordUser = $state<AdminUser | null>(null);
  let resetPasswordTempPassword = $state('');
  let activityUser = $state<AdminUser | null>(null);

  function emptyForm() {
    return {
      email: '',
      name: '',
      role: 'member' as 'admin' | 'member' | 'catalogue_manager',
      giveaway_cap_percent: 40,
      vertical_id: undefined as number | undefined,
      verticals: [] as number[]
    };
  }
  let form = $state(emptyForm());

  const usersQuery = createQuery(() => ({
    queryKey: ['admin-users', { search }],
    queryFn: () => adminUsersApi.list({ q: search || undefined, per_page: 100 })
  }));

  const verticalsQuery = createQuery(() => ({
    queryKey: ['admin-verticals'],
    queryFn: () => adminVerticalsApi.list()
  }));

  function verticalNames(row: AdminUser): string {
    const names = (verticalsQuery.data?.results ?? [])
      .filter((v) => row.verticals.includes(v.id) || row.vertical_id === v.id)
      .map((v) => v.name);
    return names.length ? names.join(', ') : '—';
  }

  function formatWhen(iso: string | null): string {
    if (!iso) return 'Never';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  }

  const saveMutation = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) =>
      editingId ? adminUsersApi.update(editingId, body) : adminUsersApi.create(body),
    onSuccess: (data: AdminUser) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      if (data.temp_password) {
        tempPassword = data.temp_password;
      } else {
        closeForm();
      }
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  const deleteMutation = createMutation(() => ({
    mutationFn: (id: number) => adminUsersApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] })
  }));

  const resetTotpMutation = createMutation(() => ({
    mutationFn: (id: number) => adminUsersApi.resetTotp(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] })
  }));

  const resetPasswordMutation = createMutation(() => ({
    mutationFn: (id: number) => adminUsersApi.resetPassword(id),
    onSuccess: (data: { temp_password: string }) => {
      resetPasswordTempPassword = data.temp_password;
    },
    onError: (err: unknown) => {
      resetPasswordUser = null;
      alert(getErrorMessage(err));
    }
  }));

  function openCreate() {
    editingId = null;
    form = emptyForm();
    formError = '';
    tempPassword = '';
    showForm = true;
  }

  function openEdit(row: AdminUser) {
    editingId = row.id;
    form = {
      email: row.email,
      name: row.name,
      role: row.role,
      giveaway_cap_percent: row.giveaway_cap_bps / 100,
      vertical_id: row.vertical_id ?? undefined,
      verticals: [...row.verticals]
    };
    formError = '';
    tempPassword = '';
    showForm = true;
  }

  const loginsQuery = createQuery(() => ({
    queryKey: ['admin-logins-recent'],
    queryFn: () => adminLoginsApi.recent({ limit: 100 })
  }));

  function closeForm() {
    showForm = false;
    tempPassword = '';
  }

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!editingId && !form.email.trim()) {
      formError = 'Email is required';
      return;
    }
    formError = '';
    const { giveaway_cap_percent, ...fields } = form;
    const body: Record<string, unknown> = {
      ...fields,
      giveaway_cap_bps: clamp(parsePercentToBps(String(giveaway_cap_percent)), 0, 10000)
    };
    if (editingId) delete body.email; // email is immutable after creation
    saveMutation.mutate(body);
  }

  function toggleVertical(id: number) {
    const idx = form.verticals.indexOf(id);
    if (idx >= 0) form.verticals.splice(idx, 1);
    else form.verticals.push(id);
  }

  function remove(row: AdminUser) {
    if (confirm(`Delete user "${row.email}"?`)) deleteMutation.mutate(row.id);
  }

  function resetTotp(row: AdminUser) {
    if (confirm(`Reset two-factor for "${row.email}"? They will re-enroll at next login.`)) {
      resetTotpMutation.mutate(row.id);
    }
  }

  function resetPassword(row: AdminUser) {
    if (
      confirm(
        `Reset password for "${row.email}"? Their existing sessions will be signed out immediately.`
      )
    ) {
      resetPasswordUser = row;
      resetPasswordTempPassword = '';
      resetPasswordMutation.mutate(row.id);
    }
  }

  function closeResetPassword() {
    resetPasswordUser = null;
    resetPasswordTempPassword = '';
  }
</script>

<div class="tym-page">
  <div class="mb-[18px] flex items-center">
    <div>
      <h2 class="tym-section-title">Users &amp; access</h2>
      <div class="tym-section-sub">
        {usersQuery.data?.count ?? 0} staff accounts &middot; role &amp; vertical access
      </div>
    </div>
  </div>

  <SearchBar bind:value={search} onCreate={openCreate} createLabel="Add user" />

  {#if usersQuery.isLoading}
    <p class="tym-loading-state">Loading users&hellip;</p>
  {:else if usersQuery.isError}
    <p class="tym-error-state">{getErrorMessage(usersQuery.error)}</p>
  {:else if usersQuery.data}
    <div class="tym-table-wrap tym-table-scroll">
      <table class="tym-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Role</th>
            <th>Verticals</th>
            <th class="num">Giveaway allowance</th>
            <th>2FA</th>
            <th>Last active</th>
            <th class="num">Actions</th>
          </tr>
        </thead>
        <tbody>
          {#each usersQuery.data.results as row (row.id)}
            <tr class="clickable" onclick={() => openEdit(row)}>
              <td>
                <div class="flex items-center gap-[11px]">
                  <span
                    class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-heading text-xs font-bold text-white"
                    style="background:{avatarColor(row.name || row.email)}"
                  >
                    {initials(row.name || row.email)}
                  </span>
                  <div>
                    <div class="text-[13.5px] font-semibold text-navy">{row.name || row.email}</div>
                    <div class="text-[11.5px] text-dark-gray">{row.email}</div>
                  </div>
                </div>
              </td>
              <td>
                <span class="tym-badge capitalize {row.role === 'admin' ? 'tym-badge-navy' : 'tym-badge-neutral'}"
                  >{row.role}</span
                >
              </td>
              <td class="muted">{verticalNames(row)}</td>
              <td class="num muted">{(row.giveaway_cap_bps / 100).toFixed(2)}%</td>
              <td class="muted">{row.totp_enabled ? 'Enrolled' : 'Not enrolled'}</td>
              <td class="muted">{formatWhen(row.last_active)}</td>
              <td class="num">
                <div class="flex justify-end gap-1.5">
                  <button
                    type="button"
                    class="btn-secondary !h-10 !w-10 !p-0"
                    onclick={(e) => {
                      e.stopPropagation();
                      activityUser = row;
                    }}
                    aria-label="Login activity"
                    title="Login activity"
                  >
                    <History class="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    class="btn-secondary !h-10 !w-10 !p-0"
                    onclick={(e) => {
                      e.stopPropagation();
                      resetPassword(row);
                    }}
                    aria-label="Reset password"
                    title="Reset password"
                  >
                    <Lock class="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    class="btn-secondary !h-10 !w-10 !p-0"
                    onclick={(e) => {
                      e.stopPropagation();
                      resetTotp(row);
                    }}
                    aria-label="Reset two-factor"
                    title="Reset two-factor"
                  >
                    <KeyRound class="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    class="btn-danger !h-10 !w-10 !p-0"
                    onclick={(e) => {
                      e.stopPropagation();
                      remove(row);
                    }}
                    aria-label="Delete"
                    title="Delete"
                  >
                    <Trash2 class="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          {:else}
            <tr><td class="empty" colspan="7">No users found.</td></tr>
          {/each}
        </tbody>
      </table>
    </div>

    <h3 class="mb-1 mt-6 font-heading text-[15px] font-bold text-navy">Login activity</h3>
    <div class="tym-section-sub mb-3">Global feed &middot; time, IP, device</div>
    {#if loginsQuery.isLoading}
      <p class="tym-loading-state">Loading logins&hellip;</p>
    {:else if loginsQuery.isError}
      <p class="tym-error-state">{getErrorMessage(loginsQuery.error)}</p>
    {:else if loginsQuery.data}
      <div class="tym-table-wrap tym-table-scroll">
        <table class="tym-table">
          <thead>
            <tr>
              <th>User</th>
              <th>When</th>
              <th>IP address</th>
              <th>Device</th>
            </tr>
          </thead>
          <tbody>
            {#each loginsQuery.data.results as row (row.id)}
              <tr>
                <td class="font-medium text-navy">{row.email ?? `#${row.user_id}`}</td>
                <td class="muted">{new Date(row.created_at).toLocaleString()}</td>
                <td class="muted font-heading">{row.ip_address || '—'}</td>
                <td>
                  <span class="tym-badge tym-badge-neutral">
                    {row.browser} &middot; {row.os} &middot; {row.device}
                  </span>
                </td>
              </tr>
            {:else}
              <tr><td class="empty" colspan="4">No logins recorded yet.</td></tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  {/if}
</div>

{#if showForm}
  <Modal title={editingId ? 'Edit user' : 'New user'} onClose={closeForm}>
    {#if tempPassword}
      <div class="space-y-4">
        <div class="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
          Share this temporary password with the user. It won't be shown again.
        </div>
        <!-- Fixed text color (not the adaptive default): bg-gray-50 stays a
             fixed near-white in both themes, so its text must too. -->
        <p class="font-mono text-lg text-center bg-gray-50 text-[#10223A] rounded-md py-3">{tempPassword}</p>
        <button type="button" class="btn-primary w-full" onclick={closeForm}>Done</button>
      </div>
    {:else}
      <form class="space-y-4" onsubmit={submit}>
        <div>
          <label class="label" for="email">Email</label>
          <input id="email" type="email" class="input" bind:value={form.email} disabled={!!editingId} />
        </div>
        <div>
          <label class="label" for="name">Name</label>
          <input id="name" class="input" bind:value={form.name} />
        </div>
        <div>
          <label class="label" for="role">Role</label>
          <select id="role" class="input" bind:value={form.role}>
            <option value="catalogue_manager">Catalogue manager</option>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div>
          <label class="label" for="giveaway_cap_percent">Discount + commission limit (%)</label>
          <input
            id="giveaway_cap_percent"
            type="number"
            class="input"
            min="0"
            max="100"
            step="0.01"
            bind:value={form.giveaway_cap_percent}
          />
          <p class="mt-1 text-xs text-dark-gray">Combined customer discount + partner commission allowance. Admins and hotel quotations are exempt.</p>
        </div>
        <div>
          <label class="label" for="vertical_id">Primary vertical</label>
          <select id="vertical_id" class="input" bind:value={form.vertical_id}>
            <option value={undefined}>-- none --</option>
            {#each verticalsQuery.data?.results ?? [] as v (v.id)}
              <option value={v.id}>{v.name}</option>
            {/each}
          </select>
        </div>
        <div>
          <span class="label">Additional verticals</span>
          <div class="flex flex-wrap gap-2">
            {#each verticalsQuery.data?.results ?? [] as v (v.id)}
              <button
                type="button"
                class="tym-chip !min-h-0 !px-2.5 !py-1 !text-xs {form.verticals.includes(v.id) ? 'active' : ''}"
                onclick={() => toggleVertical(v.id)}
              >
                {v.name}
              </button>
            {/each}
          </div>
        </div>

        {#if formError}<p class="text-sm text-red-600">{formError}</p>{/if}

        <div class="flex justify-end gap-2 pt-2">
          <button type="button" class="btn-secondary" onclick={closeForm}>Cancel</button>
          <button type="submit" class="btn-primary" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    {/if}
  </Modal>
{/if}

{#if resetPasswordUser}
  <Modal title="Reset password" onClose={closeResetPassword}>
    {#if resetPasswordTempPassword}
      <div class="space-y-4">
        <div class="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
          Share this temporary password with {resetPasswordUser.email}. It won't be shown again.
          Their existing sessions have been signed out.
        </div>
        <p class="font-mono text-lg text-center bg-gray-50 text-[#10223A] rounded-md py-3">{resetPasswordTempPassword}</p>
        <button type="button" class="btn-primary w-full" onclick={closeResetPassword}>Done</button>
      </div>
    {:else}
      <p class="text-sm text-dark-gray">Resetting password...</p>
    {/if}
  </Modal>
{/if}

{#if activityUser}
  <UserActivityModal user={activityUser} onClose={() => (activityUser = null)} />
{/if}
